import { useContext } from 'react'

import { LDFlagSet } from 'launchdarkly-react-client-sdk'

import { useFlags as useLDFlags } from './launchdarkly-client-sdk.web'
import { PsaBannerFlagType } from '../alerts/PsaBannerFlagType'
import { CustomerInstanceMap } from '../common/constants/customer'
import { DEFAULT_UNIVERSAL_SEARCH_CONFIG, UniversalSearchConfig } from '../devTools/utils'
import { LDOverrideContext } from '../ldOverride'
import { AiInTriageThreeArmVariation } from '../searchForCare/constants'
import { CategoryPriorityConfig, DEFAULT_CATEGORY_PRIORITY } from '../universalSearch/types'
import { DEFAULT_SPECULATIVE_FILTER_THRESHOLDS } from '../wellnessToolkit/constants'
import { SpeculativeSearchThresholds } from '../wellnessToolkit/types'
export interface LDFlags {
  isDisplayLanguageEnabled: boolean
  isAiInTriageEnabledThreeArm: string
  isExpandedProgressBarEnabled: boolean
  isLanguageSelectorEnabled: boolean
  isSmartlingContextCaptureEnabled: boolean
  showDevLanguages: boolean
  showNewCaseloadDashboardProviders: boolean
  showPlatformEngagementDashboard: boolean
  isMobileBrowserToAppLoginEnabled: boolean
  isIntegratedBenefitSSOEnabled: boolean
  requireEligibilityConfirmation?: {
    isEnabled: boolean
    skipIneligibleUserWithLyraCode?: boolean
  }
  languagesEnabled: string[]
  micrositeLanguagesEnabled: string[]
  customersToShowClientEAPCheck: { supportedCustomers: string[] }
  isECDCEnabled: boolean
  isGuaranteedTimeSessionTypeEnabled: boolean
  isCaregiverTrainingEnabled: boolean
  isNewRegistrationNomenclatureEnabled: boolean
  useUpdatedCareTeamPhoneNumberPage: boolean
  shouldIncludeRelID: boolean
  shouldShowPastQuartersInBCXDashboards: boolean
  showPastQuartersInLCCDashboard: boolean
  shouldShowCareNavigatorHeaderEntrypoint: boolean
  isUSCountryDisabled: boolean
  isLatestMatchesEnabled: boolean
  shouldUseNewRescheduleScreen: boolean
  isMaintenanceModeEnabled: boolean
  isSmartSchedulingOnboardingDisabledForDA: boolean
  searchForCareHeaderExperimentQ12025v2: boolean
  homebaseEssentialsCarouselExperiment: boolean
  isCoachingEntryPointEnabled: boolean
  parentToolKitEntryPointEnabled: boolean
  showInfoNeededWhenUnresolvedInfraction: boolean
  allowDemoBeforeLaunch: boolean
  shouldSendNewWLQ: boolean
  shouldRedirectWLSToCarelon: boolean
  customerInstanceMap: CustomerInstanceMap
  isSubdomainRedirectionEnabled: boolean
  maxBenefitsToShowInExplorer: number
  isClientDataShareEnabled: boolean
  enabledROISelfServe: boolean
  enableMixpanelDebug: boolean
  isPathwaysMicrositeMultiCTAEnabled: boolean
  isAmazonCostTransparencyEnabled: boolean
  isProviderProfileVideosEnabled: boolean
  showMessageProviderAlternateTimesLink: boolean
  isCHCOutage?: boolean
  isCopayForMaxCostEstimateEnabled: boolean
  shouldShowCapacityToAvailabilityRatioAlert: boolean
  showCalendarAvailabilitiesWithCapacity: boolean
  showTemplatesButtonInMessageCenter: boolean
  showDetailedWeeklyViewForCMD: boolean
  showCaseloadFromDropdown: boolean
  enableAIClientIntakeSummaryFetch: boolean
  showAIClientIntakeSummary: boolean
  shouldShowDirectProgramLinkInClientProfile: boolean
  supervisorsThatCanMarkConversationsAsRead: string[]
  isReferralEnabled: boolean
  showNotificationModalOnLoginLandingPage: boolean
  isHPIConsentWithMedicareEnabled: boolean
  showCustomNotifications: boolean
  enableAINotesFeatures: boolean
  enableExportClientRecords: boolean
  enableExportClientRecordsSumoLogging: boolean
  enableFullApiLoggingToSumo: boolean
  showAINotesFeatures: boolean
  showDevFeatureFlagContainer?: boolean
  isMobileRegistrationEnabled?: boolean
  shouldShowLCPositioning: boolean
  triageScreenersMetadataVersion: string
  isSecondaryHealthPlanCollectionFeatureEnabled?: boolean
  hideUserTestimonialsFromLCxModal: boolean
  isDedicatedCareNavigatorEnabled: boolean
  isFamilyHubEnabled: boolean
  shouldSeePendoGuides: boolean
  shouldShowCostEstimatesAsFullyCovered: boolean
  isCostMessagingEnabled: boolean
  isCustomerDynamicSessionLimitEnabled: boolean
  isPreferredNameEnabled: boolean
  shouldShowNewMinimizedIntegratedBenefitsDesign: boolean
  isElectionsEnabled: boolean
  showPickedForYouV2: boolean
  showEnhancedLCxDiagnosis: boolean
  shouldShowTriageSummary: boolean
  isInProductCalendarEnabled: boolean
  triageAnimationsDisabled: boolean
  shouldUseSierraChat: boolean
  shouldSierraChatBeEnabledOutsideOfBusinessHours: boolean
  shouldSierraChatAllowUserAuthentication: boolean
  showPickedForYouV3: boolean
  isWellnessCheckInEnabled: boolean
  routeUserDirectlyToAgentInInAppMessagingChatWindow: boolean
  chatSessionTimeoutDuration: number
  differentialPricingRatesEnabled: boolean
  differentialPricingCalculationEnabled: boolean
  isMicrositeLookAndFeelUpdateEnabled: boolean
  shouldShowCareTabInitial: boolean
  forkedTriageV2Enabled: boolean
  shouldShowAvailabilityPreference: boolean
  isSSLPinningEnabled: boolean
  enableInAppMessagingForTesting: boolean
  isSpeculativeFilterEnabled: boolean
  speculativeSearchThresholds: SpeculativeSearchThresholds
  universalSearchConfig: UniversalSearchConfig
  universalSearchCategoryPriority: CategoryPriorityConfig
  renderSaveYourSpotV2Assignment: boolean[]
  shouldShowProviderQuickNavigation: boolean
  isElectionsEntryPointEnabled: boolean
  adultTriageDisabled: boolean
  forceAdultFlowsToHPI: boolean
  showInProductCalendarAlerts: boolean
  isPhoneNumberRequiredForApptRequest: boolean
  isCookieBannerEnabled: boolean
  inProductCalendarRefreshIntervalMS: number
  isLyraForLyrians2026Enabled: boolean
  isLyraSelectEnabled: boolean
  showCostTransparency: boolean
  isInProductCalendarAddModalEnabled: boolean
  isDACalendarTransitionEnabled: boolean
  isInProductCalendarModalityEnabled: boolean
  highRiskOUSAppointmentBookingEmailAddress: string
  canSelectMultiplePrimaryNeeds: boolean
  shouldRouteToProgramAwareTriage: boolean
  emailCapturePageExperimentV2: string
  isWellnessCheckInBenefitsEntryPointEnabled: boolean
  requireUpdateNativeAppVersion: boolean
  psaBanner: PsaBannerFlagType | undefined
  showAINotesForLCMAndCLE: boolean
  isHomebaseWellnessCheckInEntrypointEnabled: boolean
  refetchOnConversationAdded: boolean
  shouldOnlySeeCareNavigatorContactScreenInPlatform: boolean
  isFavoritedProvidersEnabled: boolean
  isWellnessGoalsEnabled: boolean
  isHomebaseEssentialsEventsEnabled: boolean
  isSideNavigationBarEnabled: boolean
  parentTeenBookingImprovementsEnabled: boolean
  isTeenLedProfileBannerEnabled: boolean
  shouldShowConfirmationPageCapacityChecks: boolean
  isExpandableWorkingHoursEnabled: boolean
  isSelectWellnessToolkitsRound2Enabled: boolean
  showProviderSearchOption: boolean
  areGlobalPreferencesEnabled: boolean
  isSmartTemplatesEnabled: boolean
  shouldShowMessengerDraftsUtility: boolean
  shouldShowMessengerAIDrafts: boolean
  isAiCoachEnabled: boolean
  isAiCoachVoiceEnabled: boolean
  isSavedProviderResultsReengagementEmailsEnabled: boolean
  isGooglePerformanceEnabled: boolean
  gctProviders: string[]
  isMemberSwitchEmployerSelfServeEnabled: boolean
  isTrsSearchLocationDataEnabled: boolean
  isHideCostAndSessionLimitEnabled: boolean
  shouldShowWalmartHPICostTransparency: boolean
  is1LyraEnabledForCareRoutes: boolean | undefined
  shouldShowPostSessionAIDrafts: boolean
  isNewBenefitsPageDesignEnabled: boolean
  enableNewMultiProgramNav: boolean
  showParentLedLCTTeenBookingUpdates: boolean
  ppuiPersistedEndpointVersions?: Record<string, number>
  ppuiShouldPersistEndpoints: boolean
  careProgressSummaryUI: boolean
  googleOUSComingSoonMicrositeEnabled: boolean
  shouldRedirectToComingSoon: boolean
  daMessagingSLADisclaimer: boolean
  isDAProviderMigrated: boolean
  isDAMinorProgramEnabled: boolean
  showMemberRecommendedBadge: boolean
  clientListWithEnabledPreferredName: string[]
  showIPCSessionCreate: boolean
  notesSidePanel: {
    endofsession: boolean
    general: boolean
    initial: boolean
    messagecenter: boolean
    referral: boolean
    session: boolean
  }
  isInProductCalendarModalityEnabledS4C: boolean
  isClientDisabled: boolean
  shouldDisplayHighRiskModalForDirectBooking: boolean
  shouldUseInternationalCoachingSesssionType: boolean
  areExpandedGlobalPreferencesEnabled: boolean
  isPracticeAdminImpersonationEnabled: boolean
  isNeurodiversityCOEEnabled: boolean
  isHealthChallengesToolkitEnabled: boolean
  isWomensHealthToolkitEnabled: boolean
  isCriticalIncidentSupportToolkitEnabled: boolean
  isToolkitDirectoryPageEnabled: boolean
  shouldHideEligibilityTypeQuestion: boolean
  isCustomerTerminationModalEnabled: boolean
  isBenefitsExtensionBannerDisplayed: boolean
  essentialsInitialLoadParamEnabled: boolean
  isHealthPlanOCREnabled: boolean
  isPromotionalSMSEnabled: boolean
  showWalmartNeurodiversityContent: boolean
  shouldHideHealthPlanPrompt: boolean
  isProviderLevelFeedbackEnabled: boolean
  isDocumentCenterEnabled: boolean
  isCustomPreferencesEnabled: boolean
  showProviderAdjectives: boolean
  isSelfPayEnabled: boolean
  isAuroraEnabled: boolean
  isNotificationCenterRefreshEnabled: boolean
  isStripeTextToPayEnabled: boolean
  enableAirlineAndEAPFeaturesForNotes: boolean
  airlineCustomerValues: string[]
  isUniversalSearchEnabled: boolean
  isAttestationPropertyIgnored: boolean
  isUngatedEssentialsEventsEnabled: boolean
  shouldPopulateApptPhoneNumber: boolean
}

export const INITIAL_LD_FLAGS: LDFlags = {
  shouldOnlySeeCareNavigatorContactScreenInPlatform: false,
  shouldShowCapacityToAvailabilityRatioAlert: false,
  showInfoNeededWhenUnresolvedInfraction: false,
  allowDemoBeforeLaunch: false,
  shouldSendNewWLQ: false,
  shouldRedirectWLSToCarelon: false,
  isClientDataShareEnabled: false,
  enabledROISelfServe: false,
  enableMixpanelDebug: false,
  isPathwaysMicrositeMultiCTAEnabled: false,
  isAmazonCostTransparencyEnabled: false,
  showMessageProviderAlternateTimesLink: false,
  showCalendarAvailabilitiesWithCapacity: false,
  showTemplatesButtonInMessageCenter: false,
  showDetailedWeeklyViewForCMD: false,
  showCaseloadFromDropdown: false,
  enableAIClientIntakeSummaryFetch: false,
  enableExportClientRecords: false,
  enableExportClientRecordsSumoLogging: false,
  enableFullApiLoggingToSumo: false,
  showAIClientIntakeSummary: false,
  shouldShowDirectProgramLinkInClientProfile: false,
  supervisorsThatCanMarkConversationsAsRead: [],
  isReferralEnabled: false,
  enableAINotesFeatures: false,
  showAINotesFeatures: false,
  showDevFeatureFlagContainer: false,
  isDisplayLanguageEnabled: true,
  isAiInTriageEnabledThreeArm: AiInTriageThreeArmVariation.CONTROL,
  isMobileBrowserToAppLoginEnabled: false,
  showCustomNotifications: false,
  languagesEnabled: [],
  isUSCountryDisabled: false,
  isLatestMatchesEnabled: false,
  isMobileRegistrationEnabled: false,
  requireEligibilityConfirmation: { isEnabled: false },
  isCoachingEntryPointEnabled: false,
  parentToolKitEntryPointEnabled: false,
  customerInstanceMap: {},
  isSubdomainRedirectionEnabled: false,
  maxBenefitsToShowInExplorer: 3,
  isProviderProfileVideosEnabled: false,
  isCHCOutage: false,
  isCopayForMaxCostEstimateEnabled: false,
  isHPIConsentWithMedicareEnabled: false,
  isLanguageSelectorEnabled: false,
  isSmartlingContextCaptureEnabled: false,
  showDevLanguages: false,
  showNewCaseloadDashboardProviders: false,
  showPlatformEngagementDashboard: false,
  micrositeLanguagesEnabled: [],
  customersToShowClientEAPCheck: { supportedCustomers: [] },
  isECDCEnabled: false,
  isGuaranteedTimeSessionTypeEnabled: true,
  isCaregiverTrainingEnabled: false,
  isNewRegistrationNomenclatureEnabled: false,
  useUpdatedCareTeamPhoneNumberPage: false,
  shouldIncludeRelID: false,
  shouldShowPastQuartersInBCXDashboards: false,
  showPastQuartersInLCCDashboard: false,
  shouldShowCareNavigatorHeaderEntrypoint: false,
  shouldUseNewRescheduleScreen: false,
  isSmartSchedulingOnboardingDisabledForDA: false,
  isMaintenanceModeEnabled: false,
  showNotificationModalOnLoginLandingPage: false,
  searchForCareHeaderExperimentQ12025v2: false,
  homebaseEssentialsCarouselExperiment: false,
  shouldShowLCPositioning: false,
  triageScreenersMetadataVersion: 'latest',
  isSecondaryHealthPlanCollectionFeatureEnabled: false,
  hideUserTestimonialsFromLCxModal: false,
  isDedicatedCareNavigatorEnabled: false,
  isFamilyHubEnabled: false,
  shouldSeePendoGuides: false,
  shouldShowCostEstimatesAsFullyCovered: false,
  isCostMessagingEnabled: false,
  isCustomerDynamicSessionLimitEnabled: false,
  isPreferredNameEnabled: false,
  shouldShowNewMinimizedIntegratedBenefitsDesign: false,
  isElectionsEnabled: false,
  showPickedForYouV2: false,
  showEnhancedLCxDiagnosis: false,
  shouldShowTriageSummary: false,
  isInProductCalendarEnabled: false,
  triageAnimationsDisabled: false,
  shouldUseSierraChat: false,
  shouldSierraChatBeEnabledOutsideOfBusinessHours: false,
  shouldSierraChatAllowUserAuthentication: false,
  showPickedForYouV3: false,
  isWellnessCheckInEnabled: false,
  routeUserDirectlyToAgentInInAppMessagingChatWindow: false,
  chatSessionTimeoutDuration: 600,
  differentialPricingRatesEnabled: false,
  differentialPricingCalculationEnabled: false,
  isMicrositeLookAndFeelUpdateEnabled: false,
  shouldShowCareTabInitial: false,
  forkedTriageV2Enabled: false,
  shouldShowAvailabilityPreference: false,
  enableInAppMessagingForTesting: false,
  renderSaveYourSpotV2Assignment: [],
  isSpeculativeFilterEnabled: false,
  speculativeSearchThresholds: DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
  universalSearchConfig: DEFAULT_UNIVERSAL_SEARCH_CONFIG,
  universalSearchCategoryPriority: DEFAULT_CATEGORY_PRIORITY,
  isSSLPinningEnabled: false,
  shouldShowProviderQuickNavigation: false,
  isElectionsEntryPointEnabled: false,
  adultTriageDisabled: false,
  forceAdultFlowsToHPI: false,
  showInProductCalendarAlerts: false,
  isPhoneNumberRequiredForApptRequest: false,
  isCookieBannerEnabled: false,
  inProductCalendarRefreshIntervalMS: 300000,
  isLyraForLyrians2026Enabled: false,
  isExpandedProgressBarEnabled: false,
  isLyraSelectEnabled: false,
  showCostTransparency: false,
  isInProductCalendarAddModalEnabled: false,
  isDACalendarTransitionEnabled: false,
  isInProductCalendarModalityEnabled: false,
  highRiskOUSAppointmentBookingEmailAddress: '',
  canSelectMultiplePrimaryNeeds: false,
  shouldRouteToProgramAwareTriage: false,
  emailCapturePageExperimentV2: 'Control', // Group that is not in the experiment
  isWellnessCheckInBenefitsEntryPointEnabled: false,
  requireUpdateNativeAppVersion: false,
  psaBanner: {
    enabled: false,
  },
  showAINotesForLCMAndCLE: false,
  isHomebaseWellnessCheckInEntrypointEnabled: false,
  refetchOnConversationAdded: false,
  isFavoritedProvidersEnabled: false,
  isWellnessGoalsEnabled: false,
  isHomebaseEssentialsEventsEnabled: false,
  isSideNavigationBarEnabled: false,
  parentTeenBookingImprovementsEnabled: false,
  isTeenLedProfileBannerEnabled: false,
  shouldShowConfirmationPageCapacityChecks: false,
  isExpandableWorkingHoursEnabled: false,
  isSelectWellnessToolkitsRound2Enabled: false,
  showProviderSearchOption: false,
  areGlobalPreferencesEnabled: false,
  isSmartTemplatesEnabled: false,
  shouldShowMessengerDraftsUtility: false,
  shouldShowMessengerAIDrafts: false,
  isAiCoachEnabled: false,
  isAiCoachVoiceEnabled: false,
  isSavedProviderResultsReengagementEmailsEnabled: false,
  is1LyraEnabledForCareRoutes: undefined,
  isGooglePerformanceEnabled: false,
  gctProviders: [],
  isMemberSwitchEmployerSelfServeEnabled: false,
  isTrsSearchLocationDataEnabled: false,
  isHideCostAndSessionLimitEnabled: false,
  shouldShowWalmartHPICostTransparency: false,
  shouldShowPostSessionAIDrafts: false,
  isNewBenefitsPageDesignEnabled: false,
  enableNewMultiProgramNav: false,
  showParentLedLCTTeenBookingUpdates: false,
  ppuiPersistedEndpointVersions: undefined,
  ppuiShouldPersistEndpoints: false,
  careProgressSummaryUI: false,
  googleOUSComingSoonMicrositeEnabled: false,
  shouldRedirectToComingSoon: false,
  daMessagingSLADisclaimer: false,
  isDAProviderMigrated: false,
  isDAMinorProgramEnabled: false,
  showMemberRecommendedBadge: false,
  clientListWithEnabledPreferredName: [],
  showIPCSessionCreate: false,
  notesSidePanel: {
    endofsession: false,
    general: false,
    initial: false,
    messagecenter: false,
    referral: false,
    session: false,
  },
  isInProductCalendarModalityEnabledS4C: false,
  isClientDisabled: false,
  shouldDisplayHighRiskModalForDirectBooking: false,
  shouldUseInternationalCoachingSesssionType: false,
  isIntegratedBenefitSSOEnabled: false,
  areExpandedGlobalPreferencesEnabled: false,
  isPracticeAdminImpersonationEnabled: false,
  isNeurodiversityCOEEnabled: false,
  isHealthChallengesToolkitEnabled: false,
  isWomensHealthToolkitEnabled: false,
  isCriticalIncidentSupportToolkitEnabled: false,
  isToolkitDirectoryPageEnabled: false,
  shouldHideEligibilityTypeQuestion: false,
  isCustomerTerminationModalEnabled: false,
  isBenefitsExtensionBannerDisplayed: false,
  essentialsInitialLoadParamEnabled: false,
  isHealthPlanOCREnabled: false,
  isPromotionalSMSEnabled: false,
  showWalmartNeurodiversityContent: false,
  shouldHideHealthPlanPrompt: false,
  isProviderLevelFeedbackEnabled: false,
  isDocumentCenterEnabled: false,
  isCustomPreferencesEnabled: false,
  showProviderAdjectives: false,
  isSelfPayEnabled: false,
  isAuroraEnabled: false,
  isNotificationCenterRefreshEnabled: false,
  isStripeTextToPayEnabled: false,
  enableAirlineAndEAPFeaturesForNotes: false,
  airlineCustomerValues: [],
  isUniversalSearchEnabled: false,
  isAttestationPropertyIgnored: false,
  isUngatedEssentialsEventsEnabled: false,
  shouldPopulateApptPhoneNumber: false,
}

export const FLAGS_PARAM_NAME = 'flags'

function isDevFlagsEnabled() {
  return typeof __DEV__ !== 'undefined' && __DEV__
}

/**
 * Get flags from dev hooks:
 *  1. flags set using localstorage
 *  2. flags set using URL param
 */
export function getDevFlags(): Partial<LDFlags> {
  if (!isDevFlagsEnabled()) {
    return {}
  }

  const devFlags = []

  // try to get flags from localstorage
  if (typeof localStorage !== 'undefined' && localStorage.getItem(FLAGS_PARAM_NAME)) {
    devFlags.push(localStorage.getItem(FLAGS_PARAM_NAME))
  }

  // try to get flags from URL param
  const searchParams = new URLSearchParams(window?.location?.search)
  if (searchParams.has(FLAGS_PARAM_NAME)) {
    devFlags.push(searchParams.get(FLAGS_PARAM_NAME))
  }

  let result = {}
  try {
    // merge dev flags with original LD flags from LD server
    result = devFlags.reduce((acc, flagsStr) => {
      const flags = flagsStr ? JSON.parse(flagsStr) : {}
      return { ...acc, ...flags }
    }, {})
  } catch (e) {
    console.error('Error parsing flags param. Make sure flags has a valid JSON string.', e)
  }

  return result
}

/**
 * Dev hook to overwrite flags set on LD server.
 * They are overwritten in the following order:
 *  1. flags set using localstorage
 *  2. flags set using URL param
 *
 * @param ldFlags flags on LD server
 */
export function overrideFlagsWithSearchParams(ldFlags: LDFlags, overrideFlags: LDFlagSet): LDFlags {
  const devFlags = { ...getDevFlags(), ...overrideFlags }
  // `ldFlags` is a Proxy object that has a get trap that triggers an evaluation event when a flag is being read.
  // Here in order to read the devFlags first, we create a another Proxy object around it.
  // We can't simply return { ...ldFlags, ...devFlags } neither use Object.assign here because that causes
  // the Proxy get trap to be called for all flags, triggering an evaluation event for all flags,
  // which blows up the batch events queue and causes events to be dropped.
  return new Proxy(ldFlags, {
    get(target, prop, receiver) {
      return devFlags[prop as keyof LDFlags] ?? Reflect.get(target, prop, receiver)
    },
  })
}

const useFlagsDev = (): LDFlags => {
  const ldFlags = useLDFlags() as LDFlags
  const { overrideFlags } = useContext(LDOverrideContext)
  return overrideFlagsWithSearchParams(ldFlags, overrideFlags)
}

export const useFlags = isDevFlagsEnabled() ? useFlagsDev : (useLDFlags as () => LDFlags)

export const defaultFlags: Partial<LDFlags> = {
  speculativeSearchThresholds: DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
  universalSearchConfig: DEFAULT_UNIVERSAL_SEARCH_CONFIG,
  universalSearchCategoryPriority: DEFAULT_CATEGORY_PRIORITY,
  isPhoneNumberRequiredForApptRequest: true,
  renderSaveYourSpotV2Assignment: [],
  psaBanner: {
    enabled: false,
  },
  showMemberRecommendedBadge: false,
}
